import { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  message,
  Divider,
  Checkbox,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  encryptData,
  decryptData,
} from "../middleware/encryption";

import logo from "../assets/logo.png";
import googleLogo from "../assets/google-logo.png";

import "antd/dist/reset.css";

const { Title, Text, Link } = Typography;

export default function Login() {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const sessionEmail = sessionStorage.getItem("access_token");

    if (sessionEmail) {
      navigate("/leads");
    }

    const root = document.documentElement;

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;

      root.style.setProperty("--x", `${x}%`);
      root.style.setProperty("--y", `${y}%`);
    };

    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [navigate]);

  const handleSubmit = async (values) => {
    setLoading(true);

    const encryptedData = encryptData({
      email: values.email,
      password: values.password,
    });

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({ data: encryptedData }),
      redirect: "follow",
    };

    try {
      const response = await fetch(
        "https://api.app.exellius.com/api/auth/login",
        requestOptions
      );

      const result = await response.json();

      setLoading(false);

      if (result.data) {
        const decryptedResponse = decryptData(result.data);

        if (decryptedResponse.token) {
          sessionStorage.setItem(
            "access_token",
            decryptedResponse.token
          );

          sessionStorage.setItem(
            "role",
            decryptedResponse.user.role
          );

          sessionStorage.setItem("email", values.email);

          sessionStorage.setItem(
            "user",
            JSON.stringify(decryptedResponse.user)
          );

          message.success("Login successful");

          window.location.href = "/leads";
        } else {
          message.error(
            decryptedResponse.message || "Login failed"
          );
        }
      } else {
        message.error(result.message || "Login failed");
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      message.error("An error occurred during login");
    }
  };

  return (
    <>
      <style>
        {`
          :root {
            --primary: #9856F2;
            --secondary: #31485A;
            --bg-1: #F9FAFB;
            --bg-2: #EEF2F7;
            --text-strong: #111827;
            --text-muted: #6B7280;
            --border: #E5E7EB;
          }

          body {
            margin: 0;
            font-family: Inter, sans-serif;
            background: linear-gradient(
              180deg,
              var(--bg-1) 0%,
              var(--bg-2) 100%
            );
            overflow-x: hidden;
          }

          .login-page {
            min-height: 100vh;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            overflow: hidden;
            padding: 30px 20px;
          }

          .login-page::before {
            content: "";
            position: absolute;
            width: 600px;
            height: 600px;
            background: radial-gradient(
              circle,
              rgba(152, 86, 242, 0.18) 0%,
              rgba(152, 86, 242, 0.08) 40%,
              transparent 70%
            );
            top: var(--y, 20%);
            left: var(--x, 20%);
            transform: translate(-50%, -50%);
            filter: blur(25px);
            z-index: 0;
            transition: top 0.2s ease, left 0.2s ease;
          }

          .login-page::after {
            content: "";
            position: absolute;
            width: 500px;
            height: 500px;
            background: radial-gradient(
              circle,
              rgba(152, 86, 242, 0.12) 0%,
              transparent 70%
            );
            bottom: -120px;
            right: -120px;
            filter: blur(25px);
            z-index: 0;
          }

          .login-container {
            width: 100%;
            max-width: 530px;
            position: relative;
            z-index: 2;
          }

          .logo-wrapper {
            text-align: center;
            margin-bottom: 24px;
          }

          .logo-image {
            height: 52px;
            width: auto;
            margin-bottom: 8px;
          }

          .tagline {
            font-size: 16px;
            color: var(--text-muted);
            margin-top: 3px;
            margin-bottom: 36px;
          }

          .login-card {
            border-radius: 22px !important;
            border: none !important;
            box-shadow: 0 18px 45px rgba(0,0,0,0.06) !important;
            padding: 14px !important;
            background: #fff;
          }

          .login-title {
            margin-bottom: 6px !important;
            font-size: 28px !important;
            font-weight: 700 !important;
            color: var(--text-strong) !important;
          }

          .login-subtitle {
            color: var(--text-muted);
            font-size: 14px;
            margin-bottom: 6px;
          }

          .login-value {
            color: var(--secondary);
            font-size: 13px;
            margin-bottom: 24px;
          }

          .custom-label {
            display: block;
            margin-bottom: 8px;
            font-size: 13px;
            font-weight: 600;
            color: var(--secondary);
          }

          .custom-input {
            height: 48px;
            border-radius: 12px !important;
            border: 1px solid var(--border) !important;
            font-size: 14px;
          }

          .custom-input:hover,
          .custom-input:focus,
          .ant-input-affix-wrapper-focused {
            border-color: var(--primary) !important;
            box-shadow: 0 0 0 3px rgba(152,86,242,0.15) !important;
          }

          .forgot-link {
            color: var(--primary) !important;
            font-size: 13px;
            font-weight: 500;
          }

          .login-btn {
            height: 50px !important;
            border: none !important;
            border-radius: 12px !important;
            background: linear-gradient(
              135deg,
              #9856F2,
              #7C3AED
            ) !important;
            font-size: 15px;
            font-weight: 600;
            box-shadow: 0 6px 14px rgba(152,86,242,0.28);
          }

          .login-btn:hover {
            transform: translateY(-1px);
          }

          .google-btn {
            height: 48px;
            border-radius: 12px !important;
            border: 1px solid var(--border) !important;
            font-size: 14px;
            font-weight: 500;
            background: #fff;
          }

          .google-btn:hover {
            background: #F9FAFB !important;
            border-color: #d1d5db !important;
          }

          .footer-text {
            text-align: center;
            margin-top: 18px;
            color: var(--text-muted);
            font-size: 13px;
          }

          .footer-link {
            color: var(--primary) !important;
            font-weight: 600;
          }

          .trust-text {
            margin-top: 16px;
            text-align: center;
            color: var(--text-muted);
            font-size: 12px;
            line-height: 1.7;
          }

          .ant-divider {
            color: #9CA3AF !important;
            font-size: 12px !important;
          }

          .ant-checkbox-wrapper {
            font-size: 13px;
            color: var(--secondary);
          }

          .ant-checkbox-checked .ant-checkbox-inner {
            background-color: var(--primary) !important;
            border-color: var(--primary) !important;
          }

          @media (max-width: 576px) {
            .login-card {
              padding: 4px !important;
            }

            .login-title {
              font-size: 24px !important;
            }
          }
        `}
      </style>

      <div className="login-page">
        <div className="login-container">
          <div className="logo-wrapper">
            <img
              src={logo}
              alt="Exellius"
              className="logo-image"
            />

            <div className="tagline">
              Data Intelligence Platform
            </div>
          </div>

          <Card className="login-card">
            <Title level={2} className="login-title">
              Welcome back
            </Title>

            <div className="login-subtitle">
              Log in to your account
            </div>

            <div className="login-value">
              Access 200M+ verified B2B contacts globally
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
              <Form.Item
                label={<span className="custom-label">Email</span>}
                name="email"
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "Please enter a valid email",
                  },
                ]}
              >
                <Input
                  className="custom-input"
                  placeholder="you@company.com"
                />
              </Form.Item>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span className="custom-label">
                  Password
                </span>

                <Link
                  className="forgot-link"
                  onClick={() =>
                    navigate("/forget-password")
                  }
                >
                  Forgot password?
                </Link>
              </div>

              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Please enter your password",
                  },
                ]}
              >
                <Input.Password
                  className="custom-input"
                  placeholder="••••••••"
                />
              </Form.Item>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <Form.Item
                  name="remember"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Remember me</Checkbox>
                </Form.Item>
              </div>

              <Form.Item
                name="terms"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "You must agree to continue"
                            )
                          ),
                  },
                ]}
              >
                <Checkbox>
                  I agree to the{" "}
                  <a
                    href="/terms-and-condition"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                </Checkbox>
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <Button
                  htmlType="submit"
                  type="primary"
                  block
                  loading={loading}
                  className="login-btn"
                >
                  Log In
                </Button>
              </Form.Item>

              <Divider>OR</Divider>

              <Button
                block
                className="google-btn"
                onClick={() =>
                  (window.location.href =
                    "https://api.app.exellius.com/api/auth/google")
                }
              >
                <img
                  src={googleLogo}
                  alt="Google"
                  style={{
                    width: 18,
                    marginRight: 10,
                    verticalAlign: "middle",
                  }}
                />
                Continue with Google
              </Button>

              <div className="trust-text">
                Trusted by global sales teams
                <br />
                Secure login • Privacy protected
              </div>

              
            </Form>
          </Card>

          <div className="footer-text">
                Don’t have an account?{" "}
                <Link
                  className="footer-link"
                  onClick={() => navigate("/signup")}
                >
                  Sign up
                </Link>
              </div>
        </div>
      </div>
    </>
  );
}